import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, LinearProgress } from '@mui/material'
import { useFarmId } from '~/common/use-farm-id'
import { useSeasonList } from '~/farm/use-season-list'
import { useSeasonId } from '~/common/use-season-id'
import { useOrganizations } from '~/common/use-organizations'
import { setActiveOrgs, useOrgContextDispatch } from '~/common/context/organization/org-context'
import {
  APPBAR_HEIGHT_REM,
  APPBAR_EXTRA_DESKTOP_REM,
  CONTENT_PADDING_RBL_REM,
  CONTENT_PADDING_T_REM,
  DASHBOARD_TITLE_HEIGHT_REM,
  FULLSCREEN_HEIGHT_MD,
} from '~/common/constants/layout-constants'
import { OrgBreadcrumbs } from '~/dashboard/org-breadcrumbs'
import { useJournalEntries } from './use-journal-entries'
import { JournalControls } from './journal-controls'
import { JournalList } from './journal-list'
import { JournalDetailPanel } from './journal-detail-panel'
import { applyFilters, ALL_ENTRY_TYPES } from './journal-utils'

const JournalPageInner = ({ farmId }) => {
  const dispatch = useOrgContextDispatch()

  const ids = useMemo(() => [farmId], [farmId])
  const typeValidations = useMemo(() => ['farm'], [])
  const { data: organizations } = useOrganizations({ ids, typeValidations })

  const orderedOrgs = useMemo(
    () => ids.map(id => (organizations || []).find(o => o.id === id)).filter(Boolean),
    [ids, organizations]
  )

  useEffect(() => {
    dispatch(setActiveOrgs({ activeOrgs: orderedOrgs }))
  }, [dispatch, orderedOrgs])

  const { seasons, loading: seasonsLoading } = useSeasonList({ farmId })
  const { seasonId } = useSeasonId({ farmId, seasons, loading: seasonsLoading })

  const { entries, loading, error } = useJournalEntries({ farmId, seasonId })

  const [activeTypes, setActiveTypes] = useState(ALL_ENTRY_TYPES)
  const [searchQuery, setSearchQuery] = useState('')
  const [dateFrom, setDateFrom] = useState(null)
  const [dateTo, setDateTo] = useState(null)
  const [selectedEntry, setSelectedEntry] = useState(null)

  const filteredEntries = useMemo(
    () => applyFilters({ entries, activeTypes, searchQuery, dateFrom, dateTo }),
    [entries, activeTypes, searchQuery, dateFrom, dateTo]
  )

  const handleSelect = (entry) => {
    setSelectedEntry(entry)
  }

  return (
    <Box
      sx={{
        height: {
          xs: `calc(100vh - ${APPBAR_HEIGHT_REM} + ${APPBAR_EXTRA_DESKTOP_REM})`,
          md: `calc(100vh - ${APPBAR_HEIGHT_REM})`,
          lg: FULLSCREEN_HEIGHT_MD,
        },
        m: `-${CONTENT_PADDING_T_REM} -${CONTENT_PADDING_RBL_REM} -${CONTENT_PADDING_RBL_REM} -${CONTENT_PADDING_RBL_REM}`,
        pt: CONTENT_PADDING_T_REM,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: DASHBOARD_TITLE_HEIGHT_REM,
          mb: '0.25rem',
          pt: 1,
          flexShrink: 0,
          px: CONTENT_PADDING_RBL_REM,
        }}
      >
        <OrgBreadcrumbs baseRoute="journal" />
      </Box>

      <Box sx={{ px: CONTENT_PADDING_RBL_REM, flexShrink: 0 }}>
        <JournalControls
          activeTypes={activeTypes}
          onTypesChange={setActiveTypes}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          dateFrom={dateFrom}
          onDateFromChange={setDateFrom}
          dateTo={dateTo}
          onDateToChange={setDateTo}
        />
      </Box>

      {error && (
        <Box sx={{ px: CONTENT_PADDING_RBL_REM, mb: 1, flexShrink: 0 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          px: CONTENT_PADDING_RBL_REM,
          pb: CONTENT_PADDING_RBL_REM,
          gap: 0,
        }}
      >
        <JournalList
          entries={filteredEntries}
          loading={loading}
          selectedId={selectedEntry?.id}
          onSelect={handleSelect}
        />
        <JournalDetailPanel
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
        />
      </Box>
    </Box>
  )
}

const JournalPage = () => {
  const { farmId, loading } = useFarmId()

  if (loading) return <LinearProgress />

  if (!farmId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a farm to view the journal.</Alert>
      </Box>
    )
  }

  return <JournalPageInner farmId={farmId} />
}

export default JournalPage
